/**
 * Server functions لإدارة وسائط الهيرو من /admin/hero-media.
 *
 * - getHeroMediaUrls(market?): يقرأ هيرو السوق المحدّد، fallback إلى "*"
 *   (الافتراضيّ للجميع/ROW)، ثمّ "" (تستخدم الواجهة heroPoster المحزَّم).
 * - updateHeroMediaUrl(kind, market, url): أدمن فقط، يحفظ الرابط بمفتاح
 *   `{base}:{market}` — أو `{base}` للسوق "*" (التوافق مع المخزون القديم).
 *
 * الأسواق المعتمدة في التابات: "*" (الكلّ/ROW) + SA + AE + IQ + OM.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { assertAdmin } from "@/utils/adminAuth.server";

export const HERO_MARKETS = ["*", "SA", "AE", "KW", "QA", "BH", "OM", "IQ", "US"] as const;
export type HeroMarket = (typeof HERO_MARKETS)[number];

const BASE_KEYS = {
  desktop: "hero_video_desktop_url",
  mobile: "hero_video_mobile_url",
  poster: "hero_poster_url",
} as const;

type Kind = keyof typeof BASE_KEYS;

/** يبني المفتاح: السوق "*" يستخدم المفتاح الأساس (توافق خلفيّ). */
function keyFor(kind: Kind, market: HeroMarket): string {
  return market === "*" ? BASE_KEYS[kind] : `${BASE_KEYS[kind]}:${market}`;
}

/** السوق المعتمد للقراءة — أيّ شيء خارج القائمة → "*" (ROW وغيره). */
function normalizeMarket(m: string | undefined | null): HeroMarket {
  if (!m) return "*";
  return (HERO_MARKETS as readonly string[]).includes(m) ? (m as HeroMarket) : "*";
}

export const getHeroMediaUrls = createServerFn({ method: "GET" })
  .inputValidator((d) =>
    z
      .object({ market: z.string().optional() })
      .parse(d ?? {}),
  )
  .handler(async ({ data }) => {
    const market = normalizeMarket(data.market);

    // نطلب مفاتيح السوق + مفاتيح "*" دفعة واحدة، ثمّ نختار بأولويّة السوق.
    const wanted = (Object.keys(BASE_KEYS) as Kind[]).flatMap((k) => [
      keyFor(k, market),
      BASE_KEYS[k],
    ]);

    const { data: rows, error } = await supabaseAdmin
      .from("app_settings")
      .select("key, value")
      .in("key", wanted);

    if (error) {
      return { desktop: "", mobile: "", poster: "", market };
    }

    const map = new Map<string, string>();
    for (const row of rows ?? []) {
      const v = row.value as unknown;
      if (typeof v === "string") map.set(row.key, v);
    }

    const pick = (k: Kind) =>
      map.get(keyFor(k, market)) || map.get(BASE_KEYS[k]) || "";

    return {
      desktop: pick("desktop"),
      mobile: pick("mobile"),
      poster: pick("poster"),
      market,
    };
  });

export const updateHeroMediaUrl = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z
      .object({
        kind: z.enum(["desktop", "mobile", "poster"]),
        market: z.enum(HERO_MARKETS).default("*"),
        url: z.string().max(2048), // فارغ مسموح لإزالة الوسائط
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);

    const key = keyFor(data.kind, data.market);

    const { error } = await supabaseAdmin
      .from("app_settings")
      .upsert(
        { key, value: data.url as unknown as never },
        { onConflict: "key" },
      );

    if (error) throw new Error(error.message);
    return { ok: true, key, url: data.url, market: data.market };
  });

/** قراءة كاملة لكلّ الأسواق دفعة واحدة — للوحة الأدمن. */
export const getHeroMediaAllMarkets = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.userId);

    const allKeys = HERO_MARKETS.flatMap((m) =>
      (Object.keys(BASE_KEYS) as Kind[]).map((k) => keyFor(k, m)),
    );

    const { data: rows, error } = await supabaseAdmin
      .from("app_settings")
      .select("key, value")
      .in("key", allKeys);

    if (error) throw new Error(error.message);

    const map = new Map<string, string>();
    for (const row of rows ?? []) {
      const v = row.value as unknown;
      if (typeof v === "string") map.set(row.key, v);
    }

    const result: Record<HeroMarket, { desktop: string; mobile: string; poster: string }> =
      {} as never;
    for (const m of HERO_MARKETS) {
      result[m] = {
        desktop: map.get(keyFor("desktop", m)) ?? "",
        mobile: map.get(keyFor("mobile", m)) ?? "",
        poster: map.get(keyFor("poster", m)) ?? "",
      };
    }
    return result;
  });
